import React, {useContext, useEffect, useState} from "react";
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    Radio,
    Typography,
    useTheme,
    Chip,
    MenuItem, Menu, IconButton
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue} from "recoil";
import {FormProvider, useForm} from "react-hook-form";
import {useMutation, useQuery} from "@tanstack/react-query";
import {isMobile} from "react-device-detect";
import {AxiosError} from "axios";
import dayjs from "dayjs";
import {bookingDataValueState, preauthState, recoveryRaModeState, rentalAgreementFillingValueState, verifySignState} from "../state";
import {IDamageOperation, IDamagesList, IEquipment, IEquipmentsDefault, IEquipmentsList, IPreviewSigning} from "../../models/models";
import {DOWNLOAD_PDF_TYPE, PREVIEW_AND_SIGNING_FORM_INITIAL_VALUE} from "./config";
import {FormAutocomplete, FormDateTimePicker, FormTextField, FormRadioGroup} from "../../common/form-fields-wrappers";
import FileUploader from "../../../../util/component/FileUploader";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {
    ra,
    raPreViewDownloadPdf,
    vehicleEquipmentDefault,
    vehicleDamages,
    fetchQrCodeSignRa,
    verifySignRa,
    fetchVerifyCheckOutAdjusted,
    fetchRaRecoveryReceipt,
    raKeyless,
    getCustomerBaseById
} from "../services/http.services";
import EmptyState from "../../../../util/component/EmptyState";
import CustomLoader from "../../../../util/component/CustomLoader";
import {ELECTRIC_ENGINE_POWER_CODE, POLLING_INTERVAL_MS, TERMIC_HYBRID_ENGINE_POWER_CODE} from "../../config";
import QrCodeDialog from "../qr-code-dialog/QrCodeDialog";
import DraftTimer from "../DraftTimer/DraftTimer";
import {GB, IT, FR} from "country-flag-icons/react/1x1";
import Keyless from "../../dms-detail/tabs-section/tabs/Keyless";

/**
 * Preview Component
 */
const PreviewAndSigning = (props: { keyLess: boolean | undefined, keyLessInfo: string | undefined }) => {
    const theme = useTheme();
    const {t, i18n} = useTranslation(['network', 'translation']);
    const {t: t2} = useTranslation(["operation"])
    const appLanguage = useTranslation();
    const navigate = useNavigate();

    const {dispatchGlobalData, globalData} = useContext(GlobalContext);

    const [verifyRefetchFlag, setVerifyRefetchFlag] = useState<boolean>(false);
    const [targetDate, setTargetDate] = useState<dayjs.Dayjs | null>(null);
    const [equipments, setEquipments] = useState<IEquipmentsList | null>(null);
    const [damages, setDamages] = useState<IDamagesList | null>(null);
    const [raCreated, setRaCreated] = useState<boolean>(false);
    const [qrCodeDeviceSwitch, setQrCodeDeviceSwitch] = useState<string>('');
    const [selectedSignedDocument, setSelectedSignedDocument] = useState<boolean>(false);
    const [verifySign, setVerifySign] = useRecoilState(verifySignState);
    const bookingEntity = useRecoilValue(bookingDataValueState);
    const raFilling = useRecoilValue(rentalAgreementFillingValueState);
    const recoveryMode = useRecoilValue(recoveryRaModeState);
    const [raLang, setRaLang] = useState('it');
    const [anchorElLang, setAnchorElLang] = React.useState<null | HTMLElement>(null);
    const openLang = Boolean(anchorElLang);
    const preauthAmount = useRecoilValue(preauthState);

    const [disableSendKeyless, setDisableSendKeyless] = useState(false);

    const previewSigningDataForm = useForm<IPreviewSigning>({
        defaultValues: PREVIEW_AND_SIGNING_FORM_INITIAL_VALUE as IPreviewSigning
    });
    const [customer, setCustomer] = useState(null);

    useEffect(() => {
        const fetchCustomer = async () => {
          try {
            const response = await getCustomerBaseById(bookingEntity.raOwner.cbId);
            const customerData = response.data;
            setCustomer(customerData);
      
            // Supponiamo che la lingua sia in customerData.language
            if (customerData.data.language) {
                const langCode = customerData?.data?.language?.toLowerCase();
                //console.log('langCode:', langCode);
                switch (langCode) {
                  case 'fr':
                    //console.log('fr');
                    setRaLang('fr');
                    break;
                  case 'it':
                    //console.log('it');
                    setRaLang('it-IT');
                    break;
                  default:
                    //console.log('en');
                    setRaLang('en');
                    break;
                }
            }else{
                //console.log('default lang');
                setRaLang('en');
            }
          } catch (error) {
            console.error('Errore nel recupero del cliente:', error);
          }
        };
      
        if (bookingEntity?.raOwner?.cbId) {
          fetchCustomer();
        }
    }, [bookingEntity?.raOwner?.cbId]);

    const viewErrorMessage = (message: string) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const vehicleEquipmentDefaultQuery = useQuery(
        ['vehicleEquipmentDefault', {tenant: raFilling.vehicleDetails.tenant, plate: raFilling.vehicleDetails.plate, idRentalAgreement: bookingEntity?.idRentalAgreement}],
        () => vehicleEquipmentDefault({tenant: raFilling.vehicleDetails.tenant, plate:  raFilling.vehicleDetails.plate, idRentalAgreement: bookingEntity?.idRentalAgreement}), {
        enabled: false,
        onSuccess: (response: IEquipmentsDefault) => {
            if (!!response) {
                setEquipments(
                    {
                        equipmentsList: response.defaultEquipments,
                        vehicleEquipmentCheckComments: response.vehicleEquipmentCheckComments
                    }
                );
            }
        },
        onError: (error) => {
            viewErrorMessage('translation:error.tryLater');
        }
    })

    /**
     * Polling Query
     */
    const actionVerifyQuery = useQuery({
        // Refetch the data every interval
        refetchInterval: POLLING_INTERVAL_MS,
        enabled: verifyRefetchFlag,
        queryKey: ['verifySign', {idRentalAgreement: bookingEntity.idRentalAgreement}],
        queryFn: async () => verifySignRa(bookingEntity.idRentalAgreement),
        onSuccess: async (response: any) => {
            if (!!response.signed) {
                setVerifySign(response);
                setQrCodeDeviceSwitch('');
                setVerifyRefetchFlag(false);

                // create and send RA
                setRaCreated(true);
                handleCreateRa(null);
            }
        },
        onError: (error: any) => {
            viewErrorMessage('translation:error.tryLater');
        }
    })

    /**
     * View verify QR Code
     */
    const actionVerifyMutation = useMutation(
        verifySignRa,
        {
            onSuccess: (response: any) => {
                if (!!response.signed && raFilling.raState?.code === "ONGOING") {
                    setRaCreated(true);
                } else {
                    fetchQrCodeSignRaMutation.mutate({idRentalAgreement: bookingEntity.idRentalAgreement, language :  raLang} as any);
                }
            },
            onError: (error: any) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    const fetchQrCodeSignRaMutation = useMutation(
        fetchQrCodeSignRa,
        {
            onSuccess: (response: any) => {
                // @ts-ignore
                setQrCodeDeviceSwitch(window.appConfig.HOMEPAGE_ORIGIN + response);
                setVerifyRefetchFlag(true);

                // @ts-ignore
                setTargetDate(dayjs().add(window.appConfig.QR_CODE_PREVIEW_AND_SIGNING_TIME_VALIDITY, 'seconds'));
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        }
    );

    const vehicleDamagesQuery = useQuery(
        ['vehicleDamages', {tenant: raFilling.vehicleDetails.tenant, plate: raFilling.vehicleDetails.plate, vin: raFilling.vehicleDetails.vin, idRentalAgreement: bookingEntity?.idRentalAgreement}],
        () => vehicleDamages({tenant: raFilling.vehicleDetails.tenant, plate: raFilling.vehicleDetails.plate, vin: raFilling.vehicleDetails.vin, idRentalAgreement: bookingEntity?.idRentalAgreement}),
        {
            enabled: false,
            onSuccess: (response: any) => {
                if (!!response && !response.includes("Error")) {
                    setDamages({
                        damagesList: response
                    });
                } else {
                    viewErrorMessage('preview.error.retrieveDamagesList');
                }
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        })

    const handleClickLang = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLang(event.currentTarget);
    };
    const handleCloseLang = () => {
        setAnchorElLang(null);
    };

    const handleChangeLang = (lang: string) => {
        setRaLang(lang);
        handleCloseLang();
    }

    const renderSelectedLang = () => {
        switch (raLang) {
            case 'fr':
            case 'fr-FR':
                return (<FR style={{borderRadius: '50%'}} title={'Francese'} width={20} height={20}/>)
            case 'it':
            case 'it-IT':
                return (<IT style={{borderRadius: '50%'}} title={'Italiano'} width={20} height={20}/>)
            case 'en':
            default:
                return (<GB style={{borderRadius: '50%'}} title={'English'} width={20} height={20}/>)
        }
    }

    const handleOnUpload = (file: File) => {
        if (!!file) {
           previewSigningDataForm.setValue('signedDocument', file);
           setSelectedSignedDocument(true);
        }
    }

    const handleOnRemove = () => {
        previewSigningDataForm.setValue('signedDocument', null);
        setSelectedSignedDocument(false);
    }

    /** RA Mutation */
    const raMutation = useMutation(
        ra,
        {
            onSuccess: (response: any) => {
                setRaCreated(true);
                const raNtwOfferIdLS = localStorage.getItem('raNtwOfferId');
                if (!!raNtwOfferIdLS) {
                    const raNtwOfferId = JSON.parse(raNtwOfferIdLS);

                    delete raNtwOfferId[bookingEntity.bookingDetail.idBooking];
                    localStorage.setItem('raNtwOfferId', JSON.stringify(raNtwOfferId));
                }

                const tenants: any =
                    globalData.user.legalEntities
                        .filter((elem: any) => elem.name !== 'all')
                        .map((elem: any) => elem.name);

                if ( (previewSigningDataForm.getValues('ra.tenant') === 'drivalia_fr' || previewSigningDataForm.getValues('ra.tenant') === 'drivalia_francia') 
                    && (tenants.includes('drivalia_fr') || tenants.includes('drivalia_francia')) ) {
                // if ([ 'drivalia_fr', 'drivalia_francia' ].includes(raFilling.vehicleDetails.tenant)) {
                    if (isMobile) {
                        navigate(`/network/check-out/${encodeURIComponent(bookingEntity.idRentalAgreement)}`);
                    } else {
                        dispatchGlobalData({
                            type: 'SET_SNACKBAR',
                            payload: {
                                snackbar: {
                                    message: t('preview.message.openCheckout'),
                                    type: snackbarTypes.warning,
                                    show: true,
                                    autoClose: true
                                }
                            }
                        });
                    }
                }
            },
            onError: (error: AxiosError) => {
                setRaCreated(false);

                let mess = 'translation:error.tryLater';
                if (error.response?.status === 400 && (error.response as any)?.data?.errorDetails === "\"ERROR_INCONSISTENT_VEHICLE_STATE\"") {
                   mess = "common.error.inconsistentVehicleState";
                }else if (error.response?.status === 400 && (error.response as any)?.data?.errorDetails === "\"ERROR_ON_PRC_RECALCULATE_DROP_OFF_DATE\"") {
                   mess = "common.error.prcDropOffDate";
                }
                viewErrorMessage(mess);
            }
        });

    const raKeylessMutation = useMutation(
        raKeyless,
        {
            onSuccess: (response: any) => {
                setRaCreated(true);
                const raNtwOfferIdLS = localStorage.getItem('raNtwOfferId');
                if (!!raNtwOfferIdLS) {
                    const raNtwOfferId = JSON.parse(raNtwOfferIdLS);

                    delete raNtwOfferId[bookingEntity.bookingDetail.idBooking];
                    localStorage.setItem('raNtwOfferId', JSON.stringify(raNtwOfferId));
                }
                setDisableSendKeyless(true);
            },
            onError: (error: AxiosError) => {
                setRaCreated(false);

                let mess = 'translation:error.tryLater';
                if (error.response?.status === 400 && (error.response as any)?.data?.errorDetails === "\"ERROR_INCONSISTENT_VEHICLE_STATE\"") {
                   mess = "common.error.inconsistentVehicleState";
                }else if (error.response?.status === 400 && (error.response as any)?.data?.errorDetails === "\"ERROR_ON_PRC_RECALCULATE_DROP_OFF_DATE\"") {
                   mess = "common.error.prcDropOffDate";
                }
                viewErrorMessage(mess);
            }
        });

    /** Verify RA by RaIdCode */
    const fetchVerifyCheckOutAdjustedMutation = useMutation(
        fetchVerifyCheckOutAdjusted,
        {
            onSuccess: (response: any) => {
                if (!response.executed) {
                    const checkOutType = !!recoveryMode ? 'recovery-check-out' : 'check-out';

                    if (isMobile) {
                        navigate(`/network/${checkOutType}/${bookingEntity.idRentalAgreement}`);
                    } else {
                        // @ts-ignore
                        setQrCodeDeviceSwitch(`${window.appConfig.HOMEPAGE_ORIGIN}/network/${checkOutType}/${bookingEntity.idRentalAgreement}`);
                    }
                } else {
                    viewErrorMessage('raCheckOutIn.raVehicleIdentification.message.error.executed');
                }
            },
            onError: (error: AxiosError) => {
                let mess = '';
                // @ts-ignore
                if (error.response?.status === 404 && error.response?.data?.errorDetails === "\"ERROR_ID_RA_NOT_FOUND\"") {
                    mess = "common.error.notExistingRa"
                } else {
                    mess = 'translation:error.tryLater';
                }
                viewErrorMessage(mess);
            }
        });

    const handleViewQrCode = async () => {
        actionVerifyMutation.mutate(bookingEntity.idRentalAgreement);
    }

    const handlePreFilledRADownload = async () => {
        downloadPdfQuery.refetch();
    }

    const onCloseQrCodeDialog = () => {
        setVerifyRefetchFlag(false);
        setTargetDate(null);
        setQrCodeDeviceSwitch('');
    }

    const handlePaperCreateRa = (e: React.BaseSyntheticEvent) => {
        if (!!e) {
            e.stopPropagation();
        }

        const file: any = previewSigningDataForm.getValues('signedDocument');
        const form = new FormData();
        form.append('file', file);

        const uploadReceiptReq: any = {
            idRentalAgreement: encodeURIComponent(bookingEntity.idRentalAgreement),
            type: 'RA_SCAN',
            receipt: form
        };
        fetchRaRecoveryReceiptMutation.mutate(uploadReceiptReq);
    }

    const fetchRaRecoveryReceiptMutation = useMutation(
        fetchRaRecoveryReceipt,
        {
            onSuccess: async (response) => {
                handleCreateRa(null);
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    const handleCreateRa = async (e: React.SyntheticEvent | null) => {
        if (!!e) {
            e.stopPropagation();
        }

        raMutation.mutate({
            idRentalAgreement: bookingEntity?.idRentalAgreement,
            raState: {code: "ONGOING", description: "OnGoing"},
            language: raLang
        } as any);
    }

    const handleCreateKeylessRa = async (e: React.SyntheticEvent | null) => {
        if (!!e) {
            e.stopPropagation();
        }

        raKeylessMutation.mutate({
            idRentalAgreement: bookingEntity?.idRentalAgreement,
            raState: {code: "DRAFT", description: "DRAFT"},
            language: raLang,
            keyless: true,
            keylessInfo: props.keyLessInfo,
        } as any);
    }

    const handleCheckOutAdjusted = () => {
        fetchVerifyCheckOutAdjustedMutation.mutate({id: bookingEntity.idRentalAgreement, type: 'check-out'} as any);
    }

    const handleGoHome = () => {
        navigate(`/`)
    }

    const downloadPdfQuery = useQuery(
        [' raPreViewDownloadPdf', {idRentalAgreement: bookingEntity?.idRentalAgreement, language: appLanguage.i18n.language }],
        () =>  raPreViewDownloadPdf({
            pdfType: DOWNLOAD_PDF_TYPE.RA,
            idRentalAgreement: bookingEntity?.idRentalAgreement,
            language: raLang
        }),
        {
            enabled: false,
            onSuccess: (response: any) => {
                if (!!response) {
                    const blob = new Blob([response], { type: 'application/pdf' });
                    const pdfURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    document.body.append(link);
                    link.href = pdfURL;
                    link.download = `${bookingEntity?.idRentalAgreement}.pdf`;
                    link.click();
                }
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        })

    const qrCodeDraftTimerExpired = () => {
        onCloseQrCodeDialog();
        setTargetDate(null);

        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t('preview.message.qrCodeExpired'),
                    type: snackbarTypes.info,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    useEffect(() => {
        vehicleEquipmentDefaultQuery.refetch();
        vehicleDamagesQuery.refetch();

        // set form values
        previewSigningDataForm.setValue('ra.raNumber', bookingEntity.idRentalAgreement);
        previewSigningDataForm.setValue('ra.dmsIdentificationCode', bookingEntity.bookingDetail.pickUpLocation);
        //previewSigningDataForm.setValue('ra.pickUpDate', bookingEntity.bookingDetail.pickupDate);
        previewSigningDataForm.setValue('ra.pickUpDate', recoveryMode ? dayjs(bookingEntity.recoveryDate) : dayjs());
        previewSigningDataForm.setValue('ra.renewalsAndSubstitutions', bookingEntity.renewalsSubstitution);
        // previewSigningDataForm.setValue('ra.dmsOperatorInCharge', globalData.user.name + ' ' + globalData.user.surname);
        previewSigningDataForm.setValue('ra.dmsOperatorInCharge', globalData.user.name);

        previewSigningDataForm.setValue('vehicleState.acriss', (raFilling.vehicleDetails.acrissCode !== undefined && raFilling.vehicleDetails.acrissLabel !== undefined) ? raFilling.vehicleDetails.acrissCode + ' ' + raFilling.vehicleDetails.acrissLabel : '');
        previewSigningDataForm.setValue('vehicleState.sipp', !!raFilling.vehicleDetails.sippCode ? raFilling.vehicleDetails.sippCode + ' ' + raFilling.vehicleDetails.sippLabel : t('Sipp Less'));
        previewSigningDataForm.setValue('vehicleState.plateNumber', raFilling.vehicleDetails.plate);
        previewSigningDataForm.setValue('vehicleState.vehicleId', raFilling.vehicleDetails.vin);
        previewSigningDataForm.setValue('vehicleState.energyType', raFilling.vehicleDetails.enginePowerGroupCode);
        previewSigningDataForm.setValue('vehicleState.kmOnStart', raFilling.vehicleDetails.kms);
        previewSigningDataForm.setValue('vehicleState.fuelOnStart', !!raFilling.vehicleDetails.currentTermicTankFilling ? raFilling.vehicleDetails.currentTermicTankFilling + '/8' : '');
        previewSigningDataForm.setValue('vehicleState.batteryOnStart', !!raFilling.vehicleDetails.currentElectricTankFilling ? raFilling.vehicleDetails.currentElectricTankFilling+ ' %'  : '');
        previewSigningDataForm.setValue('vehicleState.rfidCard', !!raFilling.vehicleDetails.rfidCardNumber ? raFilling.vehicleDetails.rfidCardNumber  : '');

        previewSigningDataForm.setValue('vehicleState.chargingCableType2', !!raFilling.vehicleDetails.chargingCableType2 ? raFilling.vehicleDetails.chargingCableType2 : '');
        previewSigningDataForm.setValue('vehicleState.chargingCableDomesticType', !!raFilling.vehicleDetails.chargingCableDomesticType ? raFilling.vehicleDetails.chargingCableDomesticType : '');

        //LB
        previewSigningDataForm.setValue('vehicleState.keyless', !!raFilling.vehicleDetails.keyless ? raFilling.vehicleDetails.keyless : 'false');

        previewSigningDataForm.setValue('ra.tenant', bookingEntity.idTenant);
        previewSigningDataForm.setValue('ra.dealer', bookingEntity.dealer);

        if (!!verifySign?.signed) {
            if (raFilling.raState?.code === "ONGOING") {
                setRaCreated(true);
            }
        }

        setRaLang(i18n.language);
    }, [])

    return (
        <>
            <Typography variant={"h6"}>
                {t("preview.title.mainDriver")}
            </Typography>

            <FormProvider {...previewSigningDataForm}>
                <Grid container columnSpacing={2}>
                    {/*<Grid item xs={12}>*/}
                    {/*    <Typography variant={"h6"}>*/}
                    {/*        {t("Main Driver")}*/}
                    {/*    </Typography>*/}
                    {/*</Grid>*/}

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='ra.raNumber'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.idRentalAgreement")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormAutocomplete<IPreviewSigning>
                            name='ra.dmsIdentificationCode'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.dmsIdentificationCode")}
                            fullWidth
                            options={[]}
                            isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                            getOptionLabel={(opt: any) => opt?.description}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormDateTimePicker<IPreviewSigning>
                            name="ra.pickUpDate"
                            label={t("preview.form.label.pickUpDate")}
                            control={previewSigningDataForm.control}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='ra.renewalsAndSubstitutions'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.renewalsAndSubstitutions")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='ra.dmsOperatorInCharge'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.dmsOperatorInCharge")}
                            fullWidth
                            disabled
                        />
                    </Grid>
                </Grid>

                <Typography variant={"h6"} sx={{mt: theme.spacing(2)}}>
                    {t("preview.title.vehicleStatus")}
                </Typography>
                <Grid container columnSpacing={2}>
                    {/*<Grid item xs={12} sx={{mt: theme.spacing(4)}}>*/}
                    {/*    <Typography variant={"h6"}>*/}
                    {/*        {t("Vehicle Status")}*/}
                    {/*    </Typography>*/}
                    {/*</Grid>*/}

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='vehicleState.acriss'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.acriss")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='vehicleState.sipp'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.sipp")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='vehicleState.plateNumber'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.plateNumber")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='vehicleState.vehicleId'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.vehicleId")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='vehicleState.energyType'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.energyType")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField<IPreviewSigning>
                            name='vehicleState.kmOnStart'
                            control={previewSigningDataForm.control}
                            label={t("preview.form.label.kmOnStart")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    {
                        // TERMIC_HYBRID_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
                        TERMIC_HYBRID_ENGINE_POWER_CODE.includes(raFilling?.vehicleDetails?.enginePowerGroupCode as string) &&
                        <Grid item xs={6} md={3}>
                            <FormTextField<IPreviewSigning>
                                name='vehicleState.fuelOnStart'
                                control={previewSigningDataForm.control}
                                label={t("preview.form.label.fuelOnStart")}
                                fullWidth
                                disabled
                            />
                        </Grid>
                    }

                    {
                        // ELECTRIC_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
                        ELECTRIC_ENGINE_POWER_CODE.includes(raFilling?.vehicleDetails?.enginePowerGroupCode as string) &&
                            <Grid item xs={6} md={3}>
                                <FormTextField<IPreviewSigning>
                                    name='vehicleState.batteryOnStart'
                                    control={previewSigningDataForm.control}
                                    label={t("preview.form.label.batteryOnStart")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                    }

                    {
                        // ELECTRIC_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
                        ELECTRIC_ENGINE_POWER_CODE.includes(raFilling.vehicleDetails.enginePowerGroupCode as string) &&
                        <Grid item xs={6} md={3}>
                            <FormTextField<IPreviewSigning>
                                name='vehicleState.rfidCard'
                                control={previewSigningDataForm.control}
                                label={t("preview.form.label.rfidCard")}
                                fullWidth
                                disabled
                            />
                        </Grid>
                    }

                    {
                        // ELECTRIC_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
                        ELECTRIC_ENGINE_POWER_CODE.includes(raFilling.vehicleDetails.enginePowerGroupCode as string) &&
                        <Grid item xs={6} md={3}>
                            <FormRadioGroup<IPreviewSigning>
                                name="vehicleState.chargingCableType2"
                                label={t("preview.form.label.chargingType.2")}
                                control={previewSigningDataForm.control}
                                readOnly
                            >
                                <FormControlLabel
                                    value="NO"
                                    label={t("common.button.no")}
                                    disabled
                                    control={<Radio/>}
                                />
                                <FormControlLabel
                                    value="YES"
                                    label={t("common.button.yes")}
                                    disabled
                                    control={<Radio/>}
                                />
                            </FormRadioGroup>
                        </Grid>
                    }

                    {
                        // ELECTRIC_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
                        ELECTRIC_ENGINE_POWER_CODE.includes(raFilling.vehicleDetails.enginePowerGroupCode as string) &&
                        <Grid item xs={6} md={3}>
                            <FormRadioGroup<IPreviewSigning>
                                name="vehicleState.chargingCableDomesticType"
                                label={t("preview.form.label.chargingType.domestic")}
                                control={previewSigningDataForm.control}
                                readOnly
                            >
                                <FormControlLabel
                                    value="NO"
                                    label={t("common.button.no")}
                                    disabled
                                    control={<Radio/>}
                                />
                                <FormControlLabel
                                    value="YES"
                                    label={t("common.button.yes")}
                                    disabled
                                    control={<Radio/>}
                                />
                            </FormRadioGroup>
                        </Grid>
                    }

                        <Grid item xs={6} md={3}>
                            <FormRadioGroup<IPreviewSigning>
                                name="vehicleState.keyless"
                                label={t("preview.form.label.keyless")}
                                control={previewSigningDataForm.control}
                                readOnly
                            >
                                <FormControlLabel
                                    value="false"
                                    label={t("common.button.no")}
                                    disabled
                                    control={<Radio/>}
                                />
                                <FormControlLabel
                                    value="true"
                                    label={t("common.button.yes")}
                                    disabled
                                    control={<Radio/>}
                                />
                            </FormRadioGroup>
                        </Grid>
                </Grid>
            </FormProvider>

            <Typography variant={"h6"} sx={{my: theme.spacing(2)}}>
                {t("preview.title.damages")}
            </Typography>

            {
                (!damages || !damages.damagesList || damages?.damagesList?.length === 0)  &&
                <EmptyState
                    title={""}
                    content={t("common.emptyState")}
                />
            }
            <Grid container spacing={2}>
                {
                    !!damages?.damagesList &&
                    damages?.damagesList
                        .map((damage: IDamageOperation) =>
                            <>
                                <Grid item xs={4} md={3}>
                                    {t2(`masterDataDamage.positionName.${damage?.position?.code}`)}
                                </Grid>
                                <Grid item xs={4} md={2}>
                                    {t2(`masterDataDamage.partName.${damage?.part?.code}`)}
                                </Grid>
                                <Grid item xs={4} md={1}>
                                    <Chip label={t2(`damageList.severityValue.${damage?.severity.toLowerCase()}`)}
                                          sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}} />
                                </Grid>
                            </>
                        )
                }
            </Grid>

            <Typography variant={"h6"} sx={{mt: theme.spacing(2), mb: theme.spacing(1)}}>
                {t("preview.title.equipments")}
            </Typography>

            {
                (!equipments || !equipments.equipmentsList || equipments?.equipmentsList?.length === 0)  &&
                <EmptyState
                    title={""}
                    content={t("common.emptyState")}
                />
            }

            <Grid container spacing={2} sx={{mt: theme.spacing(1), mb: theme.spacing(4)}}>
                {/* nome/presenza */}
                {
                    !!equipments?.equipmentsList &&
                    equipments?.equipmentsList
                        .map((equipment: IEquipment) =>
                            <>
                                <Grid item xs={8} md={5}>
                                    <Typography variant={"body"}>
                                        {t(`preview.equipment.${equipment.code}`)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={4} md={1} sx={{display: "flex", justifyContent: "center"}}>
                                    {equipment.associated === true ?
                                        <Typography variant={"body"}>
                                            {t("common.button.yes")}
                                        </Typography> :
                                        <Typography variant={"body"}>
                                            {t("common.button.no")}
                                        </Typography>}
                                </Grid>
                            </>
                        )
                }
            </Grid>

            {   // Caricamento manuale per in Recovery Mode
                recoveryMode && !raCreated &&
                <Grid container>
                    <Grid xs={12}>
                        <Typography variant={"h6"} sx={{mt: theme.spacing(2), mb: theme.spacing(1)}}>
                            {t("preview.title.uploadPaperRA")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <FileUploader onUpload={(file: File) => {handleOnUpload(file)}}
                                      onRemove={handleOnRemove}
                                      acceptedFiles={{'': []}}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{display: "flex", justifyContent: 'flex-end', pt: theme.spacing(2)}}>
                        <Button sx={{mx: theme.spacing(1)}}
                                disabled={!selectedSignedDocument}
                                onClick={handlePaperCreateRa}>
                            {t('preview.form.button.createAndSendRA')}
                        </Button>
                    </Grid>
                </Grid>
            }

            {
                !recoveryMode && !verifySign.signed &&
                <Box sx={{display: 'flex', justifyContent: 'center', my: theme.spacing(2)}}>
                    <Typography>{t('preview.form.label.raLang')}</Typography>
                    <Box sx={{mr: theme.spacing(2)}}>
                        <IconButton
                            onClick={handleClickLang}
                            aria-controls={openLang ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={openLang ? 'true' : undefined}
                        >
                            {renderSelectedLang()}
                        </IconButton>
                    </Box>
                    <Menu
                        anchorEl={anchorElLang}
                        id="account-menu"
                        open={openLang}
                        onClose={handleCloseLang}
                        onClick={handleCloseLang}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                        <MenuItem onClick={() => {handleChangeLang('en')}}>
                            <GB style={{borderRadius: '50%'}} title={'English'} width={20} height={20}/>
                        </MenuItem>

                        <MenuItem onClick={() => {handleChangeLang('it')}}>
                            <IT style={{borderRadius: '50%'}} title={'Italiano'} width={20} height={20}/>
                        </MenuItem>

                        <MenuItem onClick={() => {handleChangeLang('fr')}}>
                            <FR style={{borderRadius: '50%'}} title={'Francese'} width={20} height={20}/>
                        </MenuItem>
                    </Menu>

                    {!props.keyLess && <Button sx={{mx: theme.spacing(1)}}
                             onClick={handleViewQrCode}>
                        {t('preview.form.button.digitalSign')}
                    </Button>}

                    {props.keyLess && <Button sx={{mx: theme.spacing(1)}}
                             onClick={handleCreateKeylessRa}
                             disabled={disableSendKeyless}>
                        {t('preview.form.button.sendRA')}
                    </Button>}
                </Box>
            }

            {/*{*/}
            {/*    viewSignedRaUploader &&*/}
            {/*    <Box sx={{mt: theme.spacing(2)}}>*/}
            {/*        <Box>*/}
            {/*            <FileUploader onUpload={(file: File) => {handleOnUpload(file)}}*/}
            {/*                          onRemove={handleOnRemove}*/}
            {/*                          acceptedFiles={{'': []}}*/}
            {/*            />*/}
            {/*        </Box>*/}

            {/*        <Box sx={{display: 'flex', justifyContent: 'center', mt: theme.spacing(2)}}>*/}
            {/*            /!*<Button variant={"outlined"} sx={{mx: theme.spacing(1)}}*!/*/}
            {/*            /!*        onClick={handleCancelManualSign}>*!/*/}
            {/*            /!*    {t('preview.form.button.cancel')}*!/*/}
            {/*            /!*</Button>*!/*/}

            {/*            <Button sx={{mx: theme.spacing(1)}}*/}
            {/*                    disabled={!signedDocument}*/}
            {/*                    onClick={(e: React.SyntheticEvent) => handleCreateRa(e, 'manual')}>*/}
            {/*                {t('preview.form.button.createAndSendRA')}*/}
            {/*            </Button>*/}
            {/*        </Box>*/}
            {/*    </Box>*/}
            {/*}*/}

            {
                !recoveryMode && !!verifySign.signed && !raCreated &&
                <Box sx={{display: 'flex', justifyContent: 'center', mt: theme.spacing(2)}}>
                    <Button variant={"outlined"} sx={{mx: theme.spacing(1)}}
                            onClick={handlePreFilledRADownload}>
                        {t('preview.form.button.printSignedRA')}
                    </Button>

                    <Button sx={{mx: theme.spacing(1)}}
                            onClick={handleCreateRa}>
                        {t('preview.form.button.createAndSendRA')}
                    </Button>
                </Box>
            }

            {
                raCreated &&
                <Box sx={{display: 'flex', justifyContent: 'center', mt: theme.spacing(2)}}>
                    <Button variant={"outlined"} sx={{mx: theme.spacing(1)}}
                            onClick={handlePreFilledRADownload}>
                        {t('preview.form.button.printSignedRA')}
                    </Button>

                    <Button variant={"outlined"} sx={{mx: theme.spacing(1)}}
                            onClick={handleGoHome}>
                        {t('preview.form.button.goHome')}
                    </Button>

                    <Button sx={{mx: theme.spacing(1)}}
                            onClick={handleCheckOutAdjusted}>
                        {t('preview.form.button.checkOutAdjusted')}
                    </Button>
                </Box>
            }

            {
                !!qrCodeDeviceSwitch &&
                <QrCodeDialog
                    size={512}
                    dialogTitle={t('preview.qrCodeDialog.title')}
                    backdropClick={true}
                    viewQrCodeString={false}
                    onClose={onCloseQrCodeDialog}
                    open={!!qrCodeDeviceSwitch}
                    qrCode={qrCodeDeviceSwitch}
                />
            }

            {
                !!qrCodeDeviceSwitch && !!targetDate &&
                <DraftTimer targetDate={targetDate} description={t('createRa.qrCodeValidity')} onExpired={qrCodeDraftTimerExpired}/>
            }

            <CustomLoader loading={actionVerifyMutation.isLoading || vehicleEquipmentDefaultQuery.isFetching ||
                vehicleDamagesQuery.isFetching || downloadPdfQuery.isFetching || fetchQrCodeSignRaMutation.isLoading ||
                raMutation.isLoading || fetchRaRecoveryReceiptMutation.isLoading} />
        </>
    )
}

export default PreviewAndSigning;